import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl, apiClient } from '../../api/client'
import { $api } from '../../api/queryClient'
import { invalidateAssembly } from './assembly-api'

/**
 * The meeting held on the platform (spec 2026-10-05-09): the console, the room
 * screen and the closing all read /sitting and refresh on its event stream
 * (cookie-authenticated EventSource), with a slow poll as a fallback.
 */
const BASE = '/api/v1/organizations/{orgCode}/contracts/{contractUid}'
type P = '/api/v1/organizations/{orgCode}/contracts/{contractUid}'

export function useSitting(org: string, minutesUid: string, enabled = true) {
    const queryClient = useQueryClient()
    useEffect(() => {
        if (!enabled || typeof EventSource === 'undefined') return
        const url = `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/contracts/${minutesUid}/sitting/stream`
        const es = new EventSource(url, { withCredentials: true })
        es.addEventListener('change', () => void invalidateAssembly(queryClient))
        return () => es.close()
    }, [org, minutesUid, enabled, queryClient])
    return $api.useQuery('get', `${BASE}/sitting` as `${P}/sitting`, {
        params: { path: { orgCode: org, contractUid: minutesUid } },
    }, { enabled, refetchInterval: 15000 })
}

export function useContestants(org: string, minutesUid: string, enabled: boolean) {
    return $api.useQuery('get', `${BASE}/contestants` as `${P}/contestants`, {
        params: { path: { orgCode: org, contractUid: minutesUid } },
    }, { enabled })
}

/** Every write refreshes every read of the assembly. */
export function useSittingMutations() {
    const queryClient = useQueryClient()
    const onSuccess = () => invalidateAssembly(queryClient)
    return {
        hold: $api.useMutation('post', `${BASE}/hold` as `${P}/hold`, { onSuccess }),
        checkIn: $api.useMutation('post', `${BASE}/check-ins` as `${P}/check-ins`, { onSuccess }),
        departure: $api.useMutation('post', `${BASE}/departures` as `${P}/departures`, { onSuccess }),
        bureau: $api.useMutation('put', `${BASE}/bureau` as `${P}/bureau`, { onSuccess }),
        open: $api.useMutation('post', `${BASE}/open` as `${P}/open`, { onSuccess }),
        closeVote: $api.useMutation('post', `${BASE}/close-vote` as `${P}/close-vote`, { onSuccess }),
        clerkVotes: $api.useMutation('post', `${BASE}/clerk-votes` as `${P}/clerk-votes`, { onSuccess }),
        amend: $api.useMutation('post', `${BASE}/amend` as `${P}/amend`, { onSuccess }),
        suspend: $api.useMutation('post', `${BASE}/suspend` as `${P}/suspend`, { onSuccess }),
        resume: $api.useMutation('post', `${BASE}/resume` as `${P}/resume`, { onSuccess }),
        answer: $api.useMutation('post', `${BASE}/questions/{questionUid}/answer` as `${P}/questions/{questionUid}/answer`, { onSuccess }),
        minutes: $api.useMutation('post', `${BASE}/minutes` as `${P}/minutes`, { onSuccess }),
        close: $api.useMutation('post', `${BASE}/close` as `${P}/close`, { onSuccess }),
    }
}

/** Downloads the evidence archive (ZIP). */
export async function downloadEvidence(org: string, minutesUid: string) {
    const { data, error } = await apiClient.GET(`${BASE}/evidence` as `${P}/evidence`, {
        params: { path: { orgCode: org, contractUid: minutesUid } },
        parseAs: 'blob',
    })
    if (error || !data) throw new Error('download failed')
    const url = URL.createObjectURL(data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `preuve-${minutesUid.slice(0, 8)}.zip`
    a.click()
    URL.revokeObjectURL(url)
}
